import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import * as moment from 'moment';
import { Fields } from './Fields';
import { deskingSelectors } from '../../../../../model';
import { vendorsLogoByType } from '../../../../../constants';
import { ProposalCardContainer } from '../cardContainer/ProposalCardContainer';
import {
  ContactForm,
  SaleDetailsForm,
  FinanceProgramFormValues,
  MarkupType,
  ProposalProductCardByVendorProps,
  ProposalProductCardModes,
} from '../../../../../types';
import {
  GaigAvailableAsset,
  GaigCreateQuoteAsset,
  GaigCreateQuoteOptions,
  GaigCreateQuoteResponse,
  GaigProgramCode,
} from '@trnsact/aftermarket-vendor-sdk-gaig';
import GAIG_TN_LOGO from '../../../../../../../assets/img/aftermarket-vendor-logos/gaig-tn-logo.png';
import { GET_DYNAMIC_AFTERMARKET_PRODUCT_PRICING } from 'modules/desking/api/getAftermarketProductDynamicPricing';
import { AftermarketProductVendorApiChannel } from '@trnsact/trnsact-shared-types';
import Delta from 'quill-delta';
import { useFormContext, useWatch } from 'react-hook-form';
import { formatCurrency } from 'utils';
import {
  CreditApplication,
  ProposalProduct,
  VendorOpportunity,
  VendorProfile,
} from '@trnsact/trnsact-shared-types/dist/generated';
import { v4 } from 'uuid';
import { EquipmentSection } from 'modules/desking/ui/configurationPanel/panelForm/sections';

const GAIG_CATEGORIES = [
  {
    category: 'Trailer Construction',
    assetClass: 'Z1',
  },
  {
    category: 'Sawmill',
    assetClass: 'Q1',
  },
  {
    category: 'Compact Construction',
    assetClass: 'G2',
  },
  {
    category: 'Heavy Construction',
    assetClass: 'G1',
  },
  {
    category: 'Lifts',
    assetClass: 'G3',
  },
  {
    category: 'Portable Construction',
    assetClass: 'I4',
  },
  {
    category: 'Utility Construction',
    assetClass: 'G6',
  },
  {
    category: 'Class 1-2',
    assetClass: 'V1',
  },
  {
    category: 'Class 3-5',
    assetClass: 'V2',
  },
  {
    category: 'Class 6-7',
    assetClass: 'V4',
  },
  {
    category: 'Class 8',
    assetClass: 'V6',
  },
  {
    category: 'Utility Trailer',
    assetClass: 'V7',
  },
  {
    category: 'Trailer',
    assetClass: 'V8',
  },
  {
    category: 'Semi-Trailer',
    assetClass: 'V9',
  },
];

const SOUTH_STATES = ['AL', 'CA', 'FL', 'GA', 'LA', 'MS', 'SC', 'TX'];

export const Card = ({
  mode,
  product,
  type = 'simple',
  equipmentCost,
  prisingConfig,
  productConfiguration,
  updateProductConfiguration,
}: ProposalProductCardByVendorProps) => {
  const { getValues } = useFormContext<FinanceProgramFormValues>();

  const [costIsValid, setIsCostValid] = useState(true);

  const startDealContact = getValues('contact') as ContactForm;
  const saleDetails = getValues('saleDetails') as SaleDetailsForm;

  const creditApp: CreditApplication | undefined =
    (useSelector((state: any) => state.creditApp) as CreditApplication) || undefined;
  const vendorOpportunity: VendorOpportunity | undefined =
    (useSelector((state: any) => state.vo) as VendorOpportunity) || undefined;

  const vendorProfile: VendorProfile = useSelector((state: any) => state.vp) as VendorProfile;
  const [isDynamicPricingLoading, setLoadingDynamicPricing] = useState(false);
  const products = useSelector(deskingSelectors.proposalsProducts) as ProposalProduct[];

  const { control } = useFormContext<FinanceProgramFormValues>();
  const [startDateValue, amountValue] = useWatch({ control, name: ['financeQuote.startDate', 'financeQuote.amount'] });

  const [getDynamicPricingInput, setGetDynamicPricingInput] = useState<{
    input: { aftermarketVendorApiChannel: AftermarketProductVendorApiChannel; gaigData: GaigCreateQuoteOptions };
  } | null>(null);

  const [originalParagraphDescription] = useState(
    product.aftermarketProduct?.aftermarketProductCatalogDetails?.paragraphDescription
  );

  const [originalProductSummary] = useState(
    product.aftermarketProduct?.aftermarketProductCatalogDetails?.productSummary
  );

  const createParagraphDescriptionDelta = (newConfiguration: any, finalRetailPricePerMonth: number) => {
    const paragraphDescriptionDelta = new Delta();
    const items = [];
    if (
      newConfiguration.contractResponse.data.portfolios &&
      newConfiguration.contractResponse.data.portfolios.length > 0
    ) {
      items.push(`Coverages: ${newConfiguration.contractResponse.data.portfolios[0].coverages.join(', ')}`);
    }
    if (product.aftermarketProduct?.externalId === 'GAIG_TN') {
      items.push(
        `Total Cost: ${formatCurrency(finalRetailPricePerMonth, true)}. This amount is included in the monthly payment listed.`
      );
    } else {
      items.push(
        `${formatCurrency(finalRetailPricePerMonth, true)} per month for ${newConfiguration.contractTerm} months`
      );
    }
    items.forEach(item => {
      if (_.isString(item)) {
        paragraphDescriptionDelta.insert(`${item}\n`);
      } else {
        // paragraphDescriptionDelta.insert(item.insert, item);
      }
    });
    if (product.aftermarketProduct?.externalId !== 'GAIG_TN' && originalParagraphDescription) {
      paragraphDescriptionDelta.ops = paragraphDescriptionDelta.ops.concat(originalParagraphDescription.ops);
    }
    return paragraphDescriptionDelta;
  };

  const createProductSummaryDelta = (newConfiguration: any, finalRetailPricePerMonth: number) => {
    const productSummaryDelta = new Delta();
    const items = [
      newConfiguration.selectedProgram.ProgramDescription,
      { insert: `You are covered for ${newConfiguration.coverageTerm} months `, bold: true },
    ];
    items.forEach(item => {
      if (_.isString(item)) {
        productSummaryDelta.insert(`${item}\n`);
      } else {
        productSummaryDelta.insert(item.insert, item);
      }
    });
    if (originalProductSummary) {
      productSummaryDelta.ops = productSummaryDelta.ops.concat(originalProductSummary.ops);
    }
    return productSummaryDelta;
  };

  const createProductCoverageDelta = (newConfiguration: any) => {
    const coverageDelta = new Delta();
    const bulletedItems = [
      newConfiguration.selectedProgram.ProgramDescriptionExternal,
      `Contract Dates: ${newConfiguration.contractResponse.data.ContractStartDate} to ${newConfiguration.contractResponse.data.ContractEndDate}`,
      `Coverage Term: ${productConfiguration.coverageTerm} months`,
      `Quote ID: ${newConfiguration.contractResponse.data.QuoteID}`,
    ];
    bulletedItems.forEach(item => {
      coverageDelta.insert(`${item}\n`, { list: 'bullet' });
    });
    return coverageDelta;
  };

  const [getDynamicPricing] = useLazyQuery<
    { aftermarketProductDynamicPricing: { success: boolean; errorMessage?: string; data: GaigCreateQuoteResponse } },
    { input: { aftermarketVendorApiChannel: AftermarketProductVendorApiChannel; gaigData: GaigCreateQuoteOptions } }
  >(GET_DYNAMIC_AFTERMARKET_PRODUCT_PRICING, {
    notifyOnNetworkStatusChange: true,
    onCompleted(response) {
      if (response.aftermarketProductDynamicPricing.success) {
        const totalMonthlyAmount = response?.aftermarketProductDynamicPricing?.data?.TotalMonthlyAmount || 0;
        const totalFeePerInvoice = response?.aftermarketProductDynamicPricing?.data?.TotalFeePerInvoice || 0;
        const totalFullTermAmount = response?.aftermarketProductDynamicPricing?.data?.TotalFullTermAmount || 0;

        const finalRetailPricePerMonth =
          product.aftermarketProduct?.externalId === 'GAIG_TN'
            ? totalFullTermAmount
            : totalMonthlyAmount + totalFeePerInvoice;

        const configuration = {
          ...productConfiguration,
          contractResponse: response?.aftermarketProductDynamicPricing,
        };

        const productSummaryDelta = createProductSummaryDelta(configuration, finalRetailPricePerMonth);

        try {
          _.set(product, 'aftermarketProduct.aftermarketProductCatalogDetails.productSummary', productSummaryDelta);
        } catch (e) {
          console.error('Error updating summary details', e);
        }

        const productParagraphDescriptionDelta = createParagraphDescriptionDelta(
          configuration,
          finalRetailPricePerMonth
        );
        try {
          _.set(
            product,
            'aftermarketProduct.aftermarketProductCatalogDetails.paragraphDescription',
            productParagraphDescriptionDelta
          );
        } catch (e) {
          console.error('Error updating paragraph description', e);
        }

        const coverageDelta = createProductCoverageDelta(configuration);

        try {
          _.set(product, 'aftermarketProduct.aftermarketProductCatalogDetails.coverageDetails', coverageDelta);
        } catch (e) {
          console.error('Error updating coverage details', e);
        }

        updateProductConfiguration({
          cost: finalRetailPricePerMonth,
          retailCost: finalRetailPricePerMonth,
          markup: { type: MarkupType.Flat, markup: 0 },
          contractResponse: response?.aftermarketProductDynamicPricing,
          quoteInput: getDynamicPricingInput,
          aftermarketProduct: {
            aftermarketProductCatalogDetails: {
              paragraphDescription: productParagraphDescriptionDelta,
              productSummary: productSummaryDelta,
              coverageDetails: coverageDelta,
            },
          },
        });

        setIsCostValid(true);
        setLoadingDynamicPricing(false);
      } else {
        setLoadingDynamicPricing(false);
        console.error('Error getting dynamic pricing:', response.aftermarketProductDynamicPricing.errorMessage);
        setIsCostValid(false);
      }
    },
    onError() {
      setLoadingDynamicPricing(false);
      setIsCostValid(false);
    },
  });

  const data = useMemo<{ API_RESPONSE_DATA: { programCodes: GaigProgramCode[] } }>(() => {
    let productCriteria = _.get(
      product,
      'aftermarketProduct.criteria.[0].event.params.AFTERMARKET_PRODUCT_VENDOR_API_DATA',
      null
    );
    if (!productCriteria) {
      const productFromApi = products.find(
        p => p.aftermarketProduct?.externalId === product.aftermarketProduct?.externalId
      );
      productCriteria = _.get(
        productFromApi,
        'aftermarketProduct.criteria[0].event.params.AFTERMARKET_PRODUCT_VENDOR_API_DATA',
        null
      );
    }
    return productCriteria;
  }, [products]);

  const { uniqueAvailableAssets, filteredProgramCodes } = useMemo(() => {
    let uniqueAvailableAssets: GaigAvailableAsset[] = [];
    if (!data) {
      return {
        uniqueAvailableAssets,
        filteredProgramCodes: [],
      };
    }
    data?.API_RESPONSE_DATA?.programCodes?.forEach(program => {
      program.availableAssets.forEach(asset => {
        uniqueAvailableAssets.push(asset);
      });
    });
    uniqueAvailableAssets = _.uniqBy(uniqueAvailableAssets, asset => asset.code);

    let filteredProgramCodes: GaigProgramCode[] = [];
    if (productConfiguration?.availableAsset) {
      data?.API_RESPONSE_DATA?.programCodes?.forEach(program => {
        program.availableAssets.forEach(asset => {
          if (program.ProgramCode.includes('ACVS') && !SOUTH_STATES.includes(productConfiguration.state)) {
            return;
          }
          if (program.ProgramCode.includes('ACVN') && SOUTH_STATES.includes(productConfiguration.state)) {
            return;
          }
          if (asset.code === productConfiguration.availableAsset) {
            filteredProgramCodes.push(program);
          }
        });
      });
    } else {
      data?.API_RESPONSE_DATA?.programCodes?.forEach(program => {
        filteredProgramCodes.push(program);
      });
    }
    filteredProgramCodes = _.uniqBy(filteredProgramCodes, program => program.ProgramCode);

    return {
      uniqueAvailableAssets,
      filteredProgramCodes,
    };
  }, [data, productConfiguration]);

  useEffect(() => {
    const configurationToUpdate: {
      state?: string;
      postalCode?: string;
    } = {};
    if (!productConfiguration.state && (creditApp?.state || startDealContact?.state)) {
      configurationToUpdate.state = creditApp?.state || startDealContact?.state || '';
    }
    if (!productConfiguration.postalCode && creditApp?.postalCode) {
      configurationToUpdate.postalCode = creditApp?.postalCode || '';
    }
    if (Object.keys(configurationToUpdate).length > 0) {
      updateProductConfiguration(configurationToUpdate);
    }
  });

  useEffect(() => {
    const assetAmountToUseForDynamicPricing = saleDetails?.financeAmount ?? equipmentCost ?? amountValue ?? 0;

    if (
      !productConfiguration.state ||
      !productConfiguration.postalCode ||
      productConfiguration.postalCode?.length < 5 ||
      !productConfiguration.availableAsset ||
      !productConfiguration.selectedProgram ||
      !productConfiguration.contractTerm ||
      !productConfiguration.coverageTerm ||
      !productConfiguration.billingFrequency ||
      mode === ProposalProductCardModes.Constructor
    ) {
      return;
    }

    if (!productConfiguration.selectedProgram.BillingFrequencyOptions.includes(productConfiguration.billingFrequency)) {
      setIsCostValid(false);
      return;
    }

    setLoadingDynamicPricing(true);

    const startDate = startDateValue ? moment.default(startDateValue) : moment.default();

    let contractStartDate = startDate;
    let contractEndDate = (startDateValue ? moment.default(startDateValue) : moment.default()).add(
      productConfiguration.contractTerm,
      'months'
    );

    let coverageStartDate = startDate;
    let coverageEndDate = (startDateValue ? moment.default(startDateValue) : moment.default()).add(
      productConfiguration.coverageTerm,
      'months'
    );

    const assetCategory = GAIG_CATEGORIES.find(category => category.assetClass === productConfiguration.availableAsset);

    const equipments = vendorOpportunity?.equipments || [];

    let assets: GaigCreateQuoteAsset[] = [];

    if (equipments.length > 0) {
      assets = equipments.map(e => {
        return {
          category: assetCategory?.category || productConfiguration?.availableAsset,
          assetAmount: e?.retailPrice || 0,
          programCode: productConfiguration?.programCode,
        };
      });
    } else {
      assets = [
        {
          category: assetCategory?.category || productConfiguration?.availableAsset,
          assetAmount: assetAmountToUseForDynamicPricing,
          programCode: productConfiguration?.programCode,
        },
      ];
    }

    let customerInfo = {
      customerName: creditApp?.businessName ?? `${creditApp?.firstName} ${creditApp?.lastName}`,
      customerNumber: creditApp?.id ?? v4(),
      customerAddress1: creditApp?.address ?? '',
      customerAddress2: '',
      customerCity: creditApp?.city ?? '',
      customerStateProv: productConfiguration.state ?? '',
      customerPostalCode: productConfiguration.postalCode ?? '',
      customerCountry: 'USA',
      customerContactEmail: creditApp?.email ?? '',
      customerPhoneNumber: creditApp?.phone ?? '',
    };

    // Start Deal context
    if (Object.keys(creditApp).length === 0 || !creditApp) {
      customerInfo = {
        customerName: (startDealContact.businessName as string) ?? (startDealContact.fullName as string),
        customerNumber: startDealContact.selectedContact as string,
        customerAddress1: '',
        customerAddress2: '',
        customerCity: '',
        customerStateProv: productConfiguration.state ?? '',
        customerPostalCode: productConfiguration.postalCode ?? '',
        customerCountry: 'USA',
        customerContactEmail: startDealContact.email,
        customerPhoneNumber: startDealContact.phone,
      };

      assets = [
        {
          category: assetCategory?.category || productConfiguration?.availableAsset,
          assetAmount: assetAmountToUseForDynamicPricing,
          programCode: productConfiguration?.programCode,
        },
      ];
    }

    const dpi: {
      input: { aftermarketVendorApiChannel: AftermarketProductVendorApiChannel; gaigData: GaigCreateQuoteOptions };
    } = {
      input: {
        aftermarketVendorApiChannel: AftermarketProductVendorApiChannel.Gaig,
        gaigData: {
          input: {
            quote: {
              billingFrequency: productConfiguration?.billingFrequency,
              contractStartDate: contractStartDate.toDate(),
              contractEndDate: contractEndDate.toDate(),
              coverageStartDate: coverageStartDate.toDate(),
              coverageEndDate: coverageEndDate.toDate(),
              contractNumber: v4(),
              userName: 'TRNSACT',
              customer: customerInfo,
              vendor: {
                vendorName: vendorProfile.name ?? '',
                vendorNumber: vendorProfile.id ?? '',
              },
              assets,
            },
          },
        },
      },
    };

    setGetDynamicPricingInput(dpi);

    getDynamicPricing({
      variables: dpi,
    });
  }, [
    productConfiguration?.state,
    productConfiguration?.postalCode,
    productConfiguration?.contractTerm,
    productConfiguration?.coverageTerm,
    productConfiguration?.availableAsset,
    productConfiguration?.selectedProgram,
    productConfiguration?.billingFrequency,
  ]);

  const handleStateChange = (state: string) => {
    updateProductConfiguration({
      state,
    });
  };

  const handlePostalCodeChange = (postalCode: string) => {
    updateProductConfiguration({
      postalCode,
    });
  };

  const handleAvailableAssetChange = (selectedAsset: any) => {
    updateProductConfiguration({
      availableAsset: selectedAsset,
    });
  };

  const handleSelectedProgramChange = (programCode: string) => {
    const selectedProgram = filteredProgramCodes.find(program => program.ProgramCode === programCode);

    updateProductConfiguration({
      programCode,
      selectedProgram,
    });
  };

  const handleContractTermChange = (selectedContractTerm: number) => {
    updateProductConfiguration({
      contractTerm: selectedContractTerm,
    });
  };

  const handleCoverageTermChange = (selectedCoverageTerm: number) => {
    updateProductConfiguration({
      coverageTerm: selectedCoverageTerm,
      COVERAGE_TERM: `${selectedCoverageTerm} Months`,
    });
  };

  const handleBillingFrequencyChange = (selectedBillingFrequency: number) => {
    updateProductConfiguration({
      billingFrequency: selectedBillingFrequency,
    });
  };

  useEffect(() => {
    if (filteredProgramCodes.length === 1) {
      handleSelectedProgramChange(filteredProgramCodes[0].ProgramCode);
    }
  }, []);

  return (
    <ProposalCardContainer
      type={type}
      mode={mode}
      prisingConfig={prisingConfig}
      equipmentCost={equipmentCost}
      proposalProduct={product}
      isPricingEditable={false}
      logo={product.aftermarketProduct?.externalId === 'GAIG_TN' ? GAIG_TN_LOGO : vendorsLogoByType['GAIG']}
      isDynamicPricingLoading={isDynamicPricingLoading}
      isDynamicPricingValid={costIsValid}
      productConfiguration={productConfiguration}
      updateProductConfiguration={updateProductConfiguration}
      factsSkipped={[]}
    >
      <Fields
        configuration={productConfiguration}
        uniqueAvailableAssets={uniqueAvailableAssets}
        filteredProgramCodes={filteredProgramCodes}
        handleStateChange={handleStateChange}
        handlePostalCodeChange={handlePostalCodeChange}
        handleAvailableAssetChange={handleAvailableAssetChange}
        handleSelectedProgramChange={handleSelectedProgramChange}
        handleContractTermChange={handleContractTermChange}
        handleCoverageTermChange={handleCoverageTermChange}
        handleBillingFrequencyChange={handleBillingFrequencyChange}
        isDynamicPricingLoading={isDynamicPricingLoading}
        mode={mode}
      />
    </ProposalCardContainer>
  );
};
